import React, { useMemo } from 'react';
import { BoardCell } from './BoardCell';
import { GRID_SIZE, DIFFICULTIES } from '../../lib/constants';
import { cn } from '../../lib/utils';

export function PuzzleBoard({ 
  board, 
  pieceSize, 
  onDrop, 
  onSwap,
  onRemove, 
  incorrectPiece,
  imageUrl,
  difficulty,
  showHint = false,
  isMobile = false,
  isLandscape = false,
  gridSize = GRID_SIZE,
  // Corner touch handlers for secret menu (mobile only)
  onCornerTouchStart,
  onCornerTouchMove,
  onCornerTouchEnd,
  // Tap mode props
  useTapMode = false,
  selectedPiece,
  onPieceTap,
  onCellTap,
  isPieceSelected,
  // Hint feature props
  hintTargetCell = null
}) {
  const difficultyHintOpacity = DIFFICULTIES[difficulty].hintOpacity;
  // Use hotkey hint (50% opacity) if active, otherwise use difficulty-based hint
  const hintOpacity = showHint ? 0.5 : difficultyHintOpacity;
  
  // Calculate dimensions - reduce padding in landscape mode
  const padding = isLandscape ? 12 : 24; // p-1.5 vs p-3
  const boxWidth = pieceSize * gridSize + padding + (gridSize - 1) * 2;
  
  const cells = useMemo(() => {
    const result = [];
    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        const piece = board[row]?.[col];
        const isIncorrect = incorrectPiece?.row === row && incorrectPiece?.col === col;
        const isHintTarget = hintTargetCell?.row === row && hintTargetCell?.col === col;
        result.push(
          <BoardCell
            key={`${row}-${col}`}
            row={row}
            col={col}
            piece={piece}
            pieceSize={pieceSize}
            onDrop={onDrop}
            onSwap={onSwap}
            onRemove={onRemove}
            isIncorrect={isIncorrect}
            hintImageUrl={imageUrl}
            hintOpacity={hintOpacity}
            gridSize={gridSize}
            useTapMode={useTapMode}
            hasSelection={!!selectedPiece}
            isSelected={piece && isPieceSelected?.(piece.id)}
            onPieceTap={() => piece && !piece.isCorrect && onPieceTap?.(piece, false, row, col)}
            onCellTap={() => onCellTap?.(row, col, !!piece, piece?.isCorrect)}
            isHintTarget={isHintTarget}
          />
        );
      }
    }
    return result;
  }, [board, pieceSize, onDrop, onSwap, onRemove, incorrectPiece, imageUrl, hintOpacity, gridSize, useTapMode, selectedPiece, isPieceSelected, onPieceTap, onCellTap, hintTargetCell]);
  
  return (
    <div 
      className={cn(
        "rounded-xl bg-muted/80 shadow-lg border-2 border-border",
        "backdrop-blur-sm flex items-center justify-center",
        isLandscape ? "p-1.5" : "p-3",
        isMobile && "max-w-[calc(100vw-32px)]"
      )}
      style={{
        width: boxWidth,
        height: boxWidth,
        maxWidth: isMobile ? 'calc(100vw - 32px)' : undefined,
        touchAction: 'manipulation', // Improve touch responsiveness
      }}
      // Corner touch handlers for secret menu (mobile only)
      onTouchStart={isMobile ? onCornerTouchStart : undefined}
      onTouchMove={isMobile ? onCornerTouchMove : undefined}
      onTouchEnd={isMobile ? onCornerTouchEnd : undefined}
      onTouchCancel={isMobile ? onCornerTouchEnd : undefined}
      data-testid="puzzle-board"
    >
      <div 
        className="grid gap-0.5"
        style={{
          gridTemplateColumns: `repeat(${gridSize}, ${pieceSize}px)`,
          gridTemplateRows: `repeat(${gridSize}, ${pieceSize}px)`,
        }}
      >
        {cells}
      </div>
    </div>
  );
}
